import Phaser from 'phaser';
import { getHero, HEROES } from '@/data/heroes';
import { GameState, FINAL_STAGE } from '@/state/GameState';
import { InputManager } from '@/systems/InputManager';
import { Player } from '@/entities/Player';
import { Enemy } from '@/entities/Enemy';
import { CombatSystem } from '@/systems/Combat';
import { ProjectileManager } from '@/systems/ProjectileSystem';
import { ParallaxBackground } from '@/systems/ParallaxBackground';
import { atmosphereFor, buildAtmosphereConfig } from '@/systems/Atmosphere';
import { accentsFor, flashGap, bandRect } from '@/systems/StageAccents';
import { thrownWeaponVisual } from '@/systems/ThrownWeaponVisual';
import { DepthSort } from '@/systems/DepthSort';
import { getStageConfig, type StageConfig, type StageHazard } from '@/data/stages/stageConfig';
import { VEHICLE_MOUNT_RANGE, getStageRideable } from '@/data/vehicles';
import { Vehicle } from '@/entities/Vehicle';
import { getEnemyDef, getDefaultPoiseThreshold } from '@/data/enemies';
import { HUD_REGISTRY_KEY, type HudPayload } from '@/systems/HudData';
import { SFX_1UP_KEY } from '@/systems/SfxKeys';
import { HitJuiceManager } from '@/systems/HitJuiceManager';
import { getSaveManager, ACTIVE_SLOT } from '@/systems/SaveManager';
import { hasCutscene } from '@/data/cutscene';
import { nextRoute, bossRushNextRoute, scoreAttackNextRoute } from '@/systems/stageFlow';
import {
  bossRushProgress,
  BOSS_RUSH_SEQUENCE,
  scoreAttackStageMultiplier,
} from '@/systems/BossRushSequence';
import { generateSurvivalWave } from '@/systems/SurvivalWaveGenerator';
import { survivalWaveClearBonus } from '@/systems/SurvivalScoring';
import { SURVIVAL_ENEMY_POOL, SURVIVAL_BOSS_ROSTER } from '@/data/survivalConfig';
import { TagComboTracker, type TagFinisher } from '@/systems/TagCombo';
import { TutorialHintsManager, HINT_DEFS, type HintId } from '@/systems/TutorialHints';
import type { WeaponDef } from '@/data/weapons';
import { WORLD_WEAPONS, PICKUP_RANGE } from '@/data/weapons';
import { getStallDef } from '@/data/stalls';
import type { StallPlacement } from '@/data/stalls';
import { createStallState, damageStall, resolveStallDrop } from '@/systems/StallSystem';
import type { StallState } from '@/systems/StallSystem';
import { getPropDef, PROP_DIMENSIONS } from '@/data/props';
import type { PropPlacement, HealthPickupPlacement } from '@/data/props';
import { createPropState, damageProp, resolvePropDrop } from '@/systems/PropSystem';
import type { PropState } from '@/systems/PropSystem';
import { WaveManager } from '@/systems/WaveManager';
import { BossEnemy } from '@/entities/BossEnemy';
import { createBossBrain } from '@/systems/BossRegistry';
import type { BossBrain, BossContext, BossIntent } from '@/systems/BossBrain';
import {
  advanceBossRush as advanceFinaleBossRush,
  advanceGauntletPhase,
  currentBossRushBossId,
  currentGauntletPhase,
  initialGauntletState,
  isGauntletComplete,
  respawnAtGauntletCheckpoint,
  saveGauntletCheckpoint,
  type FinaleGauntletConfig,
  type GauntletPhase,
  type GauntletState,
} from '@/systems/FinaleGauntlet';
import { difficultyMods, type DifficultyMods } from '@/systems/difficulty';
import { MusicDirectorAdapter } from '@/systems/MusicDirectorAdapter';
import { createPhaserMusicFactory } from '@/systems/createPhaserMusicFactory';
import { spriteAtlas } from '@/systems/SpriteAtlas';
import { spriteAnims } from '@/systems/SpriteAnimRegistry';
import { stageAnimCharIds } from '@/systems/stageAnimLoader';
import { EnemyHealthBarPool } from '@/systems/EnemyHealthBarPool';
import {
  createBackupMeter,
  tickBackupMeter,
  chargeBackupMeter,
  isBackupReady,
  consumeBackupMeter,
  backupFraction,
  type BackupMeterState,
} from '@/systems/BackupMeter';
import {
  createSummonState,
  addAllies,
  tickAllies,
  removeAlly,
  findNearestTargetIndex,
  type SummonState,
} from '@/systems/SummonSystem';
import { Ally } from '@/entities/Ally';
import { getHeroSummonConfig, type AllyKind } from '@/data/backupSummons';

/** Score awarded per regular enemy KO (scaled by mode multiplier in addScore). */
const ENEMY_KO_SCORE = 100;
/** Score awarded per boss KO. */
const BOSS_KO_SCORE = 2000;
/** Frames between carriage-lurch nudges (~5 s at 60 fps). */
const CARRIAGE_SWAY_INTERVAL = 300;
/** Ally lifespan in milliseconds (~10 s). */
const SUMMON_LIFESPAN_MS = 10_000;
/** Horizontal pixel nudge per lurch; direction alternates each interval. */
const CARRIAGE_SWAY_AMOUNT = 22;

class GenericBossBrain implements BossBrain {
  private attackCooldown = 45;

  tick(ctx: BossContext): BossIntent {
    const dx = ctx.targetX - ctx.selfX;
    const dy = ctx.targetY - ctx.selfY;
    const facing: 1 | -1 = dx >= 0 ? 1 : -1;
    if (ctx.isKO || ctx.isHitstunned || ctx.isBeingThrown) {
      return {
        moveX: 0,
        moveY: 0,
        facing,
        wantsAttack: false,
        attackType: null,
        wantsSummon: false,
      };
    }

    this.attackCooldown = Math.max(0, this.attackCooldown - 1);
    const closeX = Math.abs(dx) <= 82;
    const closeY = Math.abs(dy) <= 34;
    if (closeX && closeY && this.attackCooldown <= 0) {
      this.attackCooldown = Math.round(72 - ctx.aggressionScale * 24);
      return {
        moveX: 0,
        moveY: 0,
        facing,
        wantsAttack: true,
        attackType: null,
        wantsSummon: false,
      };
    }

    return {
      moveX: closeX ? 0 : Math.sign(dx),
      moveY: closeY ? 0 : Math.sign(dy),
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummon: false,
    };
  }
}

/**
 * StageScene — gameplay scene with optional 2P co-op.
 *
 * Co-op: P2 spawns automatically when GameState.p2HeroId is set.
 * Mid-stage drop-in: if coopMode is on and p2HeroId is set but P2 hasn't
 * spawned yet (e.g. they watched the opening), pressing their LIGHT or JUMP
 * key spawns them near P1.
 * Tag combos: when P1 and P2 hit the same enemy within 12 frames, a named
 * cinematic finisher fires (e.g. Ray + Errol = "Two Tone Storm").
 * Shared lives: both players draw from the same GameState.lives pool.
 * Friendly fire: toggled by GameState.friendlyFire (default off).
 *
 * Debug: [J] light  [K] heavy  [B] hitboxes  [C] clear  [X] game over.
 */
export class StageScene extends Phaser.Scene {
  private inputManager!: InputManager;

  private combat!: CombatSystem;

  private projectiles!: ProjectileManager;

  private parallax!: ParallaxBackground;

  private depthSort!: DepthSort;

  private player?: Player;

  private player2?: Player;

  private enemies: Enemy[] = [];

  private juice!: HitJuiceManager;

  private debugGfx?: Phaser.GameObjects.Graphics;

  private debugOn = false;

  private worldWeapons: Array<{
    def: WeaponDef;
    x: number;
    y: number;
    container: Phaser.GameObjects.Container;
  }> = [];

  // Stored from create() for use in update() HUD payload composition.
  private heroColor = 0xffffff;

  private heroName = '';

  private hero2Color = 0xffffff;

  private hero2Name = '';

  private stageName = '';

  private levelWidth = 0;

  private waveManager?: WaveManager;

  private finaleGauntletConfig?: FinaleGauntletConfig;

  private finaleGauntletState?: GauntletState;

  private checkpointBanner?: Phaser.GameObjects.Text;

  private playerDeathHandled = false;

  /** True once a stage/wave/boss transition or game-over has begun this scene
   *  instance — blocks re-entrant transitions and same-frame wave-clear vs KO races. */
  private transitioning = false;

  private stageCfgBossId?: string;

  private boss?: BossEnemy;

  /** Weak non-boss coda entity for the final PM Halloway phase (MY-1978). */
  private finaleHalloway?: Enemy;

  private diffMods!: DifficultyMods;

  private bossKOHandled = false;

  private musicDir!: MusicDirectorAdapter;

  private enemyHealthBarPool?: EnemyHealthBarPool;

  /** Display name of the active boss (from EnemyDef.name). */
  private bossLabel = '';

  /** Active elite enemy and its display name for the HUD bar. */
  private activeElite?: { enemy: Enemy; name: string };

  /**
   * True while a section's advance is held open waiting for its spawned mini-boss
   * elite to be KO'd. The section elite spawns AFTER the last wave's `remaining`
   * hits 0, so culling it can't corrupt the wave count.
   */
  private awaitingSectionElite = false;

  /** Invisible container the camera follows. Moves to player midpoint each frame. */
  private cameraAnchor!: Phaser.GameObjects.Container;

  /** Tag combo tracker — only active in co-op. */
  private tagTracker?: TagComboTracker;

  /** Cinematic tag-combo label (screen-pinned). */
  private tagFinisherText?: Phaser.GameObjects.Text;

  /** Game frame counter (always incrementing, used by tag combo timing). */
  private frame = 0;

  private tutorialHints?: TutorialHintsManager;

  private hintTexts = new Map<HintId, Phaser.GameObjects.Text>();

  /** True once P2 drop-in prompt has been added to the scene. */
  private dropInPromptAdded = false;

  private dropInPrompt?: Phaser.GameObjects.Text;

  /** Active stage hazard (undefined = none). Checked each frame in update(). */
  private stageHazard?: StageHazard;

  private p1Backup!: BackupMeterState;

  private p2Backup?: BackupMeterState;

  private summonState!: SummonState;

  private allyEntities!: Map<number, Ally>;

  private stallObjects: Array<{
    state: StallState;
    container: Phaser.GameObjects.Container;
    x: number;
    y: number;
    hitW: number;
    hitH: number;
  }> = [];

  private propObjects: Array<{
    state: PropState;
    container: Phaser.GameObjects.Container;
    x: number;
    y: number;
    hitW: number;
    hitH: number;
  }> = [];

  private healthPickupObjects: Array<{
    heal: number;
    container: Phaser.GameObjects.Container;
    x: number;
    y: number;
  }> = [];

  /** Stage-assigned rideable mount, or undefined when this stage has none. */
  private worldVehicle?: Vehicle;

  /** Frame counter used only by the moving-carriage hazard. */
  private swayTimer = 0;

  constructor() {
    super({ key: 'StageScene' });
  }

  preload(): void {
    // Deferred load: queue only the enemy/boss sheets needed for this stage.
    // Hero sheets were already loaded in BootScene; skip those (textures.exists guard).
    const run = GameState.current;
    const cfg = getStageConfig(run.stage);
    const survivalWave =
      run.gameMode === 'survival'
        ? generateSurvivalWave(run.survivalWave, SURVIVAL_ENEMY_POOL, SURVIVAL_BOSS_ROSTER)
        : null;
    const charIds = stageAnimCharIds(cfg, (id) => getEnemyDef(id)?.assetId);
    const hero = run.heroId ? getHero(run.heroId) : HEROES[0];
    if (hero?.assetId) charIds.add(hero.assetId);
    const p2Hero = run.p2HeroId ? getHero(run.p2HeroId) : undefined;
    if (p2Hero?.assetId) charIds.add(p2Hero.assetId);
    for (const group of survivalWave?.wave.enemies ?? []) {
      const assetId = getEnemyDef(group.id)?.assetId;
      if (assetId) charIds.add(assetId);
    }
    // Boss Rush / Survival bosses come from the sequence/generator, not cfg.bossId,
    // so the stage config alone misses their sheets — union the real boss in here
    // (deterministic: same id create() will spawn) so its anims preload.
    const extraBossId =
      run.gameMode === 'boss-rush'
        ? (bossRushProgress(run.bossRushIndex).nextBossId ?? undefined)
        : run.gameMode === 'survival'
          ? (survivalWave?.bossId ?? undefined)
          : undefined;
    if (extraBossId) {
      const a = getEnemyDef(extraBossId)?.assetId;
      if (a) charIds.add(a);
    }
    const staticSpriteIds = this.stageStaticSpriteIds(cfg, charIds, extraBossId);
    const queuedStaticSpriteIds = new Set<string>();
    for (const assetId of staticSpriteIds) {
      const entry = spriteAtlas.getEntry(assetId);
      if (!entry) continue;
      if (this.textures.exists(assetId)) {
        spriteAtlas.register(assetId);
        continue;
      }
      if (spriteAtlas.getTexture(assetId) !== null || queuedStaticSpriteIds.has(assetId)) continue;
      this.load.image(assetId, `assets/${entry.file}`);
      queuedStaticSpriteIds.add(assetId);
    }
    if (queuedStaticSpriteIds.size > 0) {
      this.load.once(Phaser.Loader.Events.COMPLETE, () => {
        for (const assetId of queuedStaticSpriteIds) {
          if (this.textures.exists(assetId)) spriteAtlas.register(assetId);
        }
      });
    }
    for (const e of spriteAnims.entriesForChars(charIds)) {
      if (this.textures.exists(e.key)) continue;
      this.load.spritesheet(e.key, `assets/${e.entry.file}`, {
        frameWidth: e.entry.fw,
        frameHeight: e.entry.fh,
      });
    }
  }

  private stageStaticSpriteIds(
    cfg: StageConfig,
    charIds: ReadonlySet<string>,
    extraBossId: string | undefined,
  ): Set<string> {
    const ids = new Set<string>();
    const add = (assetId: string | undefined): void => {
      if (assetId) ids.add(assetId);
    };
    const addEnemy = (enemyId: string | undefined): void => {
      if (!enemyId) return;
      const def = getEnemyDef(enemyId);
      add(def.assetId);
      Object.values(def.poseAssetIds ?? {}).forEach(add);
      (def.tauntAssetIds ?? []).forEach(add);
    };
    const hero = GameState.current.heroId ? getHero(GameState.current.heroId) : HEROES[0];
    add(hero?.assetId);
    Object.values(hero?.poseAssetIds ?? {}).forEach(add);
    const p2 = GameState.current.p2HeroId ? getHero(GameState.current.p2HeroId) : undefined;
    add(p2?.assetId);
    Object.values(p2?.poseAssetIds ?? {}).forEach(add);
    if (hero?.id === 'halloway' || p2?.id === 'halloway') add('manor-praetorian');

    cfg.parallax.layers.forEach((layer) => add(layer.image));
    charIds.forEach((assetId) => add(assetId));

    const sections = cfg.sections ?? [{ waves: cfg.waves }];
    for (const section of sections) {
      for (const wave of section.waves) {
        wave.enemies.forEach((group) => addEnemy(group.id));
      }
      addEnemy(section.eliteId);
    }
    addEnemy(cfg.eliteId);
    addEnemy(cfg.bossId);
    addEnemy(extraBossId);
    for (const phase of cfg.finaleGauntlet?.phases ?? []) {
      if (phase.kind === 'boss-rush') {
        phase.bossIds.forEach(addEnemy);
      } else if (phase.kind === 'praetor-handoff' || phase.kind === 'pm-halloway') {
        addEnemy(phase.nextBossId);
      }
    }

    cfg.marketStalls?.forEach((placement) => add(getStallDef(placement.stallId)?.assetId));
    cfg.props?.forEach((placement) => {
      const def = getPropDef(placement.propId);
      add(def?.assetId ?? (def ? `prop_${def.id.replace(/-/g, '_')}` : undefined));
    });
    if ((cfg.healthPickups?.length ?? 0) > 0) add('pickup_health');
    const rideable = getStageRideable(cfg.rideableId);
    add(rideable ? `vehicle_${rideable.kind}` : undefined);
    WORLD_WEAPONS.forEach((weapon) => add(`weapon_${weapon.id}`));
    Object.keys(StageScene.ALLY_STATS).forEach((kind) => add(`ally_${kind}`));
    return ids;
  }

  create(): void {
    // Register global anims for stage-specific sheets loaded in preload() above.
    // Hero anims were already registered in BootScene.create(); this handles enemies/bosses.
    for (const e of spriteAnims.entries()) {
      if (!this.textures.exists(e.key) || this.anims.exists(e.key)) continue;
      this.anims.create({
        key: e.key,
        frames: this.anims.generateFrameNumbers(e.key, { start: 0, end: e.entry.frames - 1 }),
        frameRate: e.entry.fps,
        repeat: e.entry.loop ? -1 : 0,
      });
    }
    const { width, height } = this.scale;
    const run = GameState.current;
    const hero = (run.heroId ? getHero(run.heroId) : undefined) ?? HEROES[0];
    const cfg = getStageConfig(run.stage);
    this.levelWidth = cfg.parallax.levelWidth;

    this.heroColor = hero.color;
    this.heroName = hero.name;
    // IMPORTANT: Phaser reuses the SAME StageScene instance across scene.start()
    // restarts (field initializers run once, in the constructor — NOT on restart).
    // Survival/Boss-Rush/Score-Attack self-restart via scene.start('StageScene'),
    // so EVERY mutable run-flag must be reset here or it carries over from the
    // prior run. transitioning/worldWeapons were missed and hard-locked the mode
    // loops on the second wave/stage (clearStageTransition + tick* early-return on
    // a stale transitioning===true; worldWeapons retained destroyed containers).
    this.enemies = [];
    this.boss = undefined;
    this.bossKOHandled = false;
    this.finaleGauntletConfig = undefined;
    this.finaleGauntletState = undefined;
    this.transitioning = false;
    this.activeElite = undefined;
    this.awaitingSectionElite = false;
    this.frame = 0;
    this.player2 = undefined;
    this.tutorialHints = undefined;
    this.hintTexts = new Map();
    this.stageHazard = cfg.hazard;
    this.swayTimer = 0;
    this.worldVehicle = undefined;
    this.stallObjects = [];
    this.propObjects = [];
    this.healthPickupObjects = [];
    this.worldWeapons = [];

    this.parallax = new ParallaxBackground(this, cfg.parallax);
    this.createAtmosphere(run.stage);
    this.createStageAccents(run.stage);
    this.stageName = cfg.name;

    this.cameras.main.setBounds(0, 0, this.levelWidth, height);
    this.cameras.main.setDeadzone(width * 0.3, height);

    this.inputManager = new InputManager();
    this.inputManager.loadPersisted();
    this.inputManager.attach(this, 1);
    this.inputManager.attach(this, 2);

    this.diffMods = difficultyMods(run.difficulty);
    this.combat = new CombatSystem();
    this.combat.friendlyFireEnabled = run.friendlyFire;
    this.combat.playerDamageScale = this.diffMods.playerDamageMult;
    this.projectiles = new ProjectileManager(this, this.combat);
    this.depthSort = new DepthSort();
    this.juice = new HitJuiceManager(this);

    this.player = new Player(this, width * 0.3, height * 0.6, hero, this.inputManager, 1);
    this.player.setWorldWidth(this.levelWidth);
    this.player.attachCombat(this.combat);
    this.player.attachProjectileManager(this.projectiles);
    this.combat.register(this.player);
    this.p1Backup = createBackupMeter();
    this.depthSort.register(this.player);
    this.summonState = createSummonState();
    this.allyEntities = new Map();

    // Camera anchor — follows midpoint of active players.
    this.cameraAnchor = this.add.container(this.player.x, this.player.y);
    this.cameras.main.startFollow(this.cameraAnchor, true, 0.08, 0.08);

    // Wire co-op P2 if hero was selected in CharSelect.
    if (run.coopMode && run.p2HeroId) {
      this.spawnPlayer2(run.p2HeroId, width * 0.5, height * 0.6);
    }

    // onHitConnect: juice + tag combo tracking + backup meter charging.
    this.combat.onHitConnect = (spec, hitX, targetY, attacker, target) => {
      this.juice.triggerHit(spec.damage, hitX, targetY);
      this.trackTagCombo(attacker?.combatId, target?.combatId);
      if (attacker === this.player) chargeBackupMeter(this.p1Backup);
      if (attacker === this.player2 && this.p2Backup) chargeBackupMeter(this.p2Backup);
      if (target === this.player) chargeBackupMeter(this.p1Backup);
      if (target === this.player2 && this.p2Backup) chargeBackupMeter(this.p2Backup);
    };

    // Survival mode: build a single escalating wave from the generator each time
    // StageScene (re)starts; survivalWave grows in GameState across the run.
    const survival = run.gameMode === 'survival';
    const bossRush = run.gameMode === 'boss-rush';
    const finaleGauntlet =
      run.gameMode === 'campaign' && run.stage === FINAL_STAGE ? cfg.finaleGauntlet : undefined;
    this.finaleGauntletConfig = finaleGauntlet;
    this.finaleGauntletState = finaleGauntlet ? initialGauntletState() : undefined;
    const survivalWave = survival
      ? generateSurvivalWave(run.survivalWave, SURVIVAL_ENEMY_POOL, SURVIVAL_BOSS_ROSTER)
      : null;
    // Boss Rush: no regular waves — a single empty wave clears instantly so the
    // sequence boss (at bossRushIndex) spawns immediately via the stage-complete path.
    const brBossId = bossRush ? bossRushProgress(run.bossRushIndex).nextBossId : null;
    const sections = bossRush
      ? [{ waves: [{ enemies: [] }] }]
      : survivalWave
        ? [{ waves: [survivalWave.wave] }]
        : (cfg.sections ?? [{ waves: cfg.waves }]);
    this.waveManager = new WaveManager(
      sections,
      this.diffMods.enemyCountMult,
      survival || bossRush ? 0 : GameState.current.checkpointSection,
    );
    this.stageCfgBossId = bossRush
      ? (brBossId ?? undefined)
      : survivalWave
        ? (survivalWave.bossId ?? undefined)
        : cfg.bossId;
    this.playerDeathHandled = false;
    this.spawnCurrentWave();

    this.musicDir = new MusicDirectorAdapter(undefined, createPhaserMusicFactory(this));
    this.musicDir.startStage(run.stage);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.musicDir.stopAll());

    // Spawn market stalls (Camden Stage 1 only; other stages have no marketStalls).
    if (cfg.marketStalls) {
      this.spawnMarketStalls(cfg.marketStalls, height);
    }

    // Spawn generic destructible props (bins, crates, phone boxes, etc.) on any stage.
    if (cfg.props) {
      this.spawnProps(cfg.props, height);
    }

    // Spawn standalone health pickups — heal on contact, no smashing needed.
    if (cfg.healthPickups) {
      this.spawnHealthPickups(cfg.healthPickups, height);
    }

    // Spawn the stage-assigned rideable mount (if any).
    this.spawnStageRideable(cfg, height);

    // Scatter a few weapon pickups across the level.
    const weaponSpawnXs = [this.levelWidth * 0.25, this.levelWidth * 0.5, this.levelWidth * 0.72];
    weaponSpawnXs.forEach((wx, i) => {
      this.spawnWorldWeapon(wx, height * 0.65, WORLD_WEAPONS[i % WORLD_WEAPONS.length]);
    });

    // Wire the player's pickup query to the world weapon list.
    if (this.player) {
      this.player.attachPickupSource((px) => this.findNearestWorldWeapon(px));
    }

    // Re-spawn dropped/broken weapons as world pickups.
    this.events.on('weapon-dropped', (payload: { x: number; y: number; def: WeaponDef }) => {
      this.spawnWorldWeapon(payload.x, payload.y, payload.def);
    });

    // Cosmetic fly-across for thrown weapons (gameplay hit is the instant hitbox).
    this.events.on(
      'weapon-thrown',
      (p: { fromX: number; fromY: number; toX: number; toY: number; def: WeaponDef }) => {
        this.spawnThrownWeaponVisual(p.fromX, p.fromY, p.toX, p.toY, p.def);
      },
    );

    // Mode loops self-restart via scene.start('StageScene'), which reuses this
    // scene's event emitter — remove our listeners on shutdown so they don't
    // stack (otherwise one super/drop/boss-summon fires N× after N restarts).
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off('weapon-dropped');
      this.events.off('weapon-thrown');
      this.events.off('player-super');
      this.events.off('boss-wants-summon');
    });

    this.debugGfx = this.add.graphics().setDepth(10000).setScrollFactor(0);

    // Cinematic tag-combo finisher label.
    this.tagFinisherText = this.add
      .text(width / 2, height / 2 - 40, '', {
        fontFamily: '"Courier New", monospace',
        fontSize: '30px',
        color: '#ffd700',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(900)
      .setAlpha(0);

    // Checkpoint banner — shown whenever a section clears.
    this.checkpointBanner = this.add
      .text(width / 2, height / 2 - 60, '— CHECKPOINT —', {
        fontFamily: '"Courier New", monospace',
        fontSize: '32px',
        color: '#00ffd4',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(910)
      .setAlpha(0);

    // Tutorial hints — shown on first play of Stage 1
    if (run.stage === 1) {
      this.tutorialHints = new TutorialHintsManager();
      this.buildTutorialHints(width, height);
    }

    this.enemyHealthBarPool = new EnemyHealthBarPool(this);
    this.bossLabel = '';

    this.writeHudRegistry();
    this.scene.launch('HudScene');

    this.events.on('player-super', (payload?: { animKey?: string }) => {
      const cam = this.cameras.main;
      cam.zoomTo(
        1.35,
        100,
        'Linear',
        true,
        (_cam: Phaser.Cameras.Scene2D.Camera, progress: number) => {
          if (progress >= 1) cam.zoomTo(1, 200, 'Linear', true);
        },
      );
      this.cameras.main.flash(120, 255, 220, 80, true);
      this.completeHint('SPECIAL');
      if (payload?.animKey === 'super_halloway') this.spawnCallSecurityGag();
    });

    this.add
      .text(
        width / 2,
        height - 36,
        'WASD move  SPACE jump  J=light  K=heavy(hold)  B boxes  [C] clear [X] over',
        {
          fontFamily: '"Courier New", monospace',
          fontSize: '14px',
          color: '#888',
        },
      )
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(500);

    this.input.keyboard?.on('keydown-B', () => {
      this.debugOn = !this.debugOn;
      if (!this.debugOn) this.debugGfx?.clear();
    });

    this.input.keyboard?.on('keydown-C', () => {
      GameState.addScore(1000);
      this.clearStageTransition();
    });

    this.input.keyboard?.on('keydown-X', () => {
      while (!GameState.isGameOver) GameState.loseLife();
      this.scene.stop('HudScene');
      this.scene.start('ResultsScene');
    });

    this.input.keyboard?.on('keydown-ESC', () => {
      if (!this.scene.isActive('PauseScene')) {
        this.scene.pause();
        this.scene.launch('PauseScene');
      }
    });
  }

  update(_time: number, delta: number): void {
    this.frame += 1;
    this.musicDir.update(delta);
    this.parallax.update();

    const frozen = this.combat.isInHitpause || this.juice.isInHitstop;
    if (frozen) {
      this.combat.tickHitpause();
      this.juice.tick();
    } else {
      this.player?.update();
      this.player2?.update();
      this.enemies.forEach((e) => e.update());
      for (const ally of this.allyEntities.values()) ally.update();
      this.boss?.update();
      this.combat.update();
      this.projectiles.update(this.combat.getCombatants(), this.levelWidth);
      this.cullKOEnemies();
      this.tickStallHits();
      this.tickPropHits();
      this.tickHealthPickups();
      this.tickPlayerDeath();
      this.tickWaveAdvance();
      this.tickBossCheck();
      this.tickTutorialHints();
      this.tickVehicleMount();
      if (this.stageHazard === 'moving-carriage') this.tickCarriageSway();
      this.tickBackupMeters();
      this.tickAllyTargets();
      this.tickAllyLifespans(delta);
    }

    this.updateCameraAnchor();
    this.checkDropIn();
    this.depthSort.update();
    this.enemyHealthBarPool?.update(this.enemies, this.boss, this.bossLabel);
    this.writeHudRegistry();

    if (this.debugOn) this.drawDebug();
  }

  // ── Market stalls ────────────────────────────────────────────────────────

  private spawnMarketStalls(placements: readonly StallPlacement[], height: number): void {
    const stallY = height * 0.62;
    const stallW = 48;
    const stallH = 36;
    placements.forEach((p) => {
      const def = getStallDef(p.stallId);
      if (!def) return;
      const sx = p.xFrac * this.levelWidth;
      const texKey = def.assetId ? spriteAtlas.getTexture(def.assetId) : null;
      const visual = texKey
        ? this.add.image(0, 0, texKey).setDisplaySize(stallW, stallH)
        : this.add.rectangle(0, 0, stallW, stallH, def.color).setStrokeStyle(2, 0xffffff);
      const label = this.add
        .text(0, -26, def.label, { fontSize: '9px', color: '#ffffff', align: 'center' })
        .setOrigin(0.5);
      const container = this.add.container(sx, stallY, [visual, label]).setDepth(2);
      this.stallObjects.push({
        state: createStallState(def),
        container,
        x: sx,
        y: stallY,
        hitW: stallW,
        hitH: stallH,
      });
    });
  }

  private tickStallHits(): void {
    if (this.stallObjects.length === 0) return;
    const activeHitboxes = this.combat.activeHitboxes;
    if (activeHitboxes.length === 0) return;

    for (const stall of this.stallObjects) {
      if (stall.state.destroyed) continue;
      const sx = stall.x - stall.hitW / 2;
      const sy = stall.y - stall.hitH / 2;
      for (const hb of activeHitboxes) {
        const { x, y, w, h } = hb.box;
        const overlaps = x < sx + stall.hitW && x + w > sx && y < sy + stall.hitH && y + h > sy;
        if (overlaps) {
          const destroyed = damageStall(stall.state);
          if (destroyed) this.destroyStall(stall);
          break; // one hit per frame per stall
        }
      }
    }
    this.stallObjects = this.stallObjects.filter((s) => !s.state.destroyed);
  }

  private destroyStall(stall: {
    state: StallState;
    container: Phaser.GameObjects.Container;
    x: number;
    y: number;
  }): void {
    stall.container.destroy();
    const drop = resolveStallDrop(stall.state.def);
    if (!drop) return;
    if (drop.kind === 'weapon') {
      this.spawnWorldWeapon(stall.x, stall.y, drop.def);
    } else if (drop.kind === 'food') {
      const p1 = this.player;
      const p2 = this.player2;
      const nearest =
        p2 && Math.abs(p2.x - stall.x) < Math.abs((p1?.x ?? Infinity) - stall.x) ? p2 : p1;
      nearest?.health.heal(drop.heal);
    }
  }

  // ── Generic props ─────────────────────────────────────────────────────────

  /**
   * Create the stage's ambient weather/particle overlay (rain/fog/embers/neon
   * dust). Screen-pinned, additive on top of the parallax backdrop. No-op for
   * stages whose atmosphere is 'none' (e.g. the Tube interior).
   */
  private createAtmosphere(stage: number): void {
    const spec = buildAtmosphereConfig(atmosphereFor(stage), this.scale.width, this.scale.height);
    if (!spec) return;
    if (!this.textures.exists('atmo-particle')) {
      const g = this.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillRect(0, 0, 4, 4);
      g.generateTexture('atmo-particle', 4, 4);
      g.destroy();
    }
    this.add
      .particles(
        0,
        0,
        'atmo-particle',
        spec.emitter as unknown as Phaser.Types.GameObjects.Particles.ParticleEmitterConfig,
      )
      .setScrollFactor(0)
      .setDepth(spec.depth);
  }

  /**
   * Create the stage's animated background accents (neon pulse, light sweeps,
   * storm lightning) — code-driven set dressing over the static backdrop.
   * Screen-pinned + additive; no-op for stages with no accents.
   */
  private createStageAccents(stage: number): void {
    const { width, height } = this.scale;
    for (const accent of accentsFor(stage)) {
      if (accent.kind === 'neonPulse') {
        const { y, h } = bandRect(accent.topFrac, accent.bottomFrac, height);
        const glow = this.add
          .rectangle(width / 2, y, width, h, accent.tint)
          .setScrollFactor(0)
          .setDepth(accent.depth)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setAlpha(accent.baseAlpha);
        this.tweens.add({
          targets: glow,
          alpha: accent.peakAlpha,
          duration: accent.periodMs,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.InOut',
        });
      } else if (accent.kind === 'lightSweep') {
        const { y, h } = bandRect(accent.topFrac, accent.bottomFrac, height);
        const sw = Math.max(8, width * accent.widthFrac);
        const streak = this.add
          .rectangle(-sw, y, sw, h, accent.tint)
          .setScrollFactor(0)
          .setDepth(accent.depth)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setAlpha(accent.peakAlpha)
          .setAngle(12);
        this.tweens.add({
          targets: streak,
          x: width + sw,
          duration: accent.sweepMs,
          repeat: -1,
          repeatDelay: accent.gapMs,
          ease: 'Sine.In',
        });
      } else if (accent.kind === 'waterShimmer') {
        const { y, h } = bandRect(accent.topFrac, accent.bottomFrac, height);
        for (let i = 0; i < accent.glints; i += 1) {
          const gx = ((i + 0.5) / accent.glints) * width;
          const gy = y - h / 2 + ((i * 37) % Math.max(1, Math.round(h)));
          const glint = this.add
            .rectangle(gx, gy, 22, 2, accent.tint)
            .setScrollFactor(0)
            .setDepth(accent.depth)
            .setBlendMode(Phaser.BlendModes.ADD)
            .setAlpha(0);
          this.tweens.add({
            targets: glint,
            alpha: accent.peakAlpha,
            duration: accent.periodMs / 2,
            yoyo: true,
            repeat: -1,
            delay: (i / accent.glints) * accent.periodMs,
            ease: 'Sine.InOut',
          });
        }
      } else if (accent.kind === 'passingTraffic') {
        // Distant vehicle lights crossing the far road — alternating directions
        // (headlights → / taillights ←) on a staggered loop. Scene-owned tweens,
        // so Phaser clears them on a mode restart (no manual cleanup).
        const { y, h } = bandRect(accent.topFrac, accent.bottomFrac, height);
        for (let i = 0; i < accent.count; i += 1) {
          const dir = i % 2 === 0 ? 1 : -1;
          const tint = dir > 0 ? accent.headTint : accent.tailTint;
          const cy = y - h / 2 + ((i * 29) % Math.max(1, Math.round(h)));
          const startX = dir > 0 ? -24 : width + 24;
          const endX = dir > 0 ? width + 24 : -24;
          const car = this.add
            .rectangle(startX, cy, 18, 3, tint)
            .setScrollFactor(0)
            .setDepth(accent.depth)
            .setBlendMode(Phaser.BlendModes.ADD)
            .setAlpha(accent.peakAlpha);
          this.tweens.add({
            targets: car,
            x: endX,
            duration: accent.crossMs,
            repeat: -1,
            repeatDelay: accent.gapMs,
            delay: (i / Math.max(1, accent.count)) * accent.gapMs,
            ease: 'Linear',
          });
        }
      } else if (accent.kind === 'driftHaze') {
        // Slow drifting fog/haze puffs with a gentle alpha pulse — pale tint added
        // at low alpha for a soft mist. Scene-owned tweens (cleared on restart).
        const { y, h } = bandRect(accent.topFrac, accent.bottomFrac, height);
        const pw = Math.max(120, width * 0.34);
        const ph = Math.max(24, h * 0.6);
        for (let i = 0; i < accent.puffs; i += 1) {
          const py = y - h / 2 + ((i * 53) % Math.max(1, Math.round(h)));
          const startX = ((i + 0.5) / accent.puffs) * (width + pw) - pw / 2;
          const puff = this.add
            .ellipse(startX, py, pw, ph, accent.tint)
            .setScrollFactor(0)
            .setDepth(accent.depth)
            .setBlendMode(Phaser.BlendModes.ADD)
            .setAlpha(0);
          this.tweens.add({
            targets: puff,
            alpha: accent.peakAlpha,
            duration: accent.periodMs / 2,
            yoyo: true,
            repeat: -1,
            delay: (i / accent.puffs) * accent.periodMs,
            ease: 'Sine.InOut',
          });
          this.tweens.add({
            targets: puff,
            x: startX + pw * 0.5,
            duration: accent.driftMs,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.InOut',
          });
        }
      } else {
        // lightningFlash — full-screen brighten on a random cadence.
        const flash = this.add
          .rectangle(width / 2, height / 2, width, height, accent.tint)
          .setScrollFactor(0)
          .setDepth(8)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setAlpha(0);
        const schedule = (): void => {
          const gap = flashGap(accent, Phaser.Math.FloatBetween(0, 1));
          this.time.delayedCall(gap, () => {
            this.tweens.add({
              targets: flash,
              alpha: accent.peakAlpha,
              duration: accent.flashMs / 2,
              yoyo: true,
              repeat: 1,
              onComplete: () => {
                flash.setAlpha(0);
                schedule();
              },
            });
          });
        };
        schedule();
      }
    }
  }

  private spawnProps(placements: readonly PropPlacement[], height: number): void {
    const propY = height * 0.62;
    placements.forEach((p) => {
      const def = getPropDef(p.propId);
      if (!def) return;
      const dims = PROP_DIMENSIONS[def.kind];
      const px = p.xFrac * this.levelWidth;
      // Explicit assetId wins; otherwise fall back to the `prop_<id>` sprite
      // convention (getTexture returns null when unregistered → colour-rect).
      const propAsset = def.assetId ?? `prop_${def.id.replace(/-/g, '_')}`;
      const texKey = spriteAtlas.getTexture(propAsset);
      const visual = texKey
        ? this.add.image(0, 0, texKey).setDisplaySize(dims.w, dims.h)
        : this.add.rectangle(0, 0, dims.w, dims.h, def.color).setStrokeStyle(2, 0xffffff);
      const label = this.add
        .text(0, -(dims.h / 2 + 10), def.label, {
          fontSize: '9px',
          color: '#ffffff',
          align: 'center',
        })
        .setOrigin(0.5);
      const container = this.add.container(px, propY, [visual, label]).setDepth(2);
      this.propObjects.push({
        state: createPropState(def),
        container,
        x: px,
        y: propY,
        hitW: dims.w,
        hitH: dims.h,
      });
    });
  }

  private tickPropHits(): void {
    if (this.propObjects.length === 0) return;
    const activeHitboxes = this.combat.activeHitboxes;
    if (activeHitboxes.length === 0) return;

    for (const prop of this.propObjects) {
      if (prop.state.destroyed) continue;
      const px = prop.x - prop.hitW / 2;
      const py = prop.y - prop.hitH / 2;
      for (const hb of activeHitboxes) {
        const { x, y, w, h } = hb.box;
        const overlaps = x < px + prop.hitW && x + w > px && y < py + prop.hitH && y + h > py;
        if (overlaps) {
          const destroyed = damageProp(prop.state);
          if (destroyed) this.destroyProp(prop);
          break; // one hit per frame per prop
        }
      }
    }
    this.propObjects = this.propObjects.filter((p) => !p.state.destroyed);
  }

  private destroyProp(prop: {
    state: PropState;
    container: Phaser.GameObjects.Container;
    x: number;
    y: number;
  }): void {
    prop.container.destroy();
    const drop = resolvePropDrop(prop.state.def);
    if (!drop) return;
    if (drop.kind === 'weapon') {
      this.spawnWorldWeapon(prop.x, prop.y, drop.def);
    } else if (drop.kind === 'food') {
      const p1 = this.player;
      const p2 = this.player2;
      const nearest =
        p2 && Math.abs(p2.x - prop.x) < Math.abs((p1?.x ?? Infinity) - prop.x) ? p2 : p1;
      nearest?.health.heal(drop.heal);
    }
  }

  // ── Health pickups ────────────────────────────────────────────────────────

  private spawnHealthPickups(placements: readonly HealthPickupPlacement[], height: number): void {
    const pickupY = height * 0.62;
    placements.forEach((p) => {
      const px = p.xFrac * this.levelWidth;
      // pickup_health food sprite if registered (null-safe → green-rect + "+").
      const texKey = spriteAtlas.getTexture('pickup_health');
      const visual = texKey
        ? this.add.image(0, 0, texKey).setDisplaySize(24, 24)
        : this.add.rectangle(0, 0, 20, 20, 0x00c864).setStrokeStyle(2, 0xffffff);
      const label = this.add
        .text(0, 0, texKey ? '' : '+', { fontSize: '16px', color: '#ffffff', align: 'center' })
        .setOrigin(0.5);
      const container = this.add.container(px, pickupY, [visual, label]).setDepth(2);
      this.healthPickupObjects.push({ heal: p.heal, container, x: px, y: pickupY });
    });
  }

  private tickHealthPickups(): void {
    if (this.healthPickupObjects.length === 0) return;
    const RANGE = 50;
    const p1 = this.player;
    const p2 = this.player2;

    this.healthPickupObjects = this.healthPickupObjects.filter((pickup) => {
      const collector =
        p1 && Math.abs(p1.x - pickup.x) <= RANGE && Math.abs(p1.y - pickup.y) <= RANGE
          ? p1
          : p2 && Math.abs(p2.x - pickup.x) <= RANGE && Math.abs(p2.y - pickup.y) <= RANGE
            ? p2
            : null;
      if (!collector) return true;
      collector.health.heal(pickup.heal);
      pickup.container.destroy();
      return false;
    });
  }

  // ── Stage rideable mount ─────────────────────────────────────────────────

  private spawnStageRideable(cfg: StageConfig, height: number): void {
    const def = getStageRideable(cfg.rideableId);
    if (!def) return;
    const vx = this.levelWidth * 0.5;
    const vy = height * 0.62;
    this.worldVehicle = new Vehicle(this, vx, vy, def);
    this.worldVehicle.setDepth(3);
  }

  /** Check proximity + PICKUP input to trigger player mount on the stage vehicle. */
  private tickVehicleMount(): void {
    if (!this.worldVehicle || this.worldVehicle.isMounted) return;
    const vx = this.worldVehicle.x;
    const vy = this.worldVehicle.y;
    const candidates: Array<{ player: Player; id: 1 | 2 }> = [];
    if (this.player) candidates.push({ player: this.player, id: 1 });
    if (this.player2) candidates.push({ player: this.player2, id: 2 });
    for (const { player: p, id } of candidates) {
      if (
        Math.abs(p.x - vx) < VEHICLE_MOUNT_RANGE &&
        Math.abs(p.y - vy) < VEHICLE_MOUNT_RANGE &&
        this.inputManager.justDown(id, 'PICKUP')
      ) {
        p.mount(this.worldVehicle, this.worldVehicle.def);
        break;
      }
    }
  }

  // ── Tutorial hints ───────────────────────────────────────────────────────

  private buildTutorialHints(width: number, height: number): void {
    const hintY = height - 72;
    HINT_DEFS.forEach((def, i) => {
      const offsetX = (i - 1) * 260;
      const t = this.add
        .text(width / 2 + offsetX, hintY, def.label, {
          fontFamily: '"Courier New", monospace',
          fontSize: '15px',
          color: '#ffffff',
          stroke: '#000000',
          strokeThickness: 2,
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(600)
        .setAlpha(0.35);
      this.hintTexts.set(def.id, t);
    });
  }

  private tickTutorialHints(): void {
    if (!this.tutorialHints || this.tutorialHints.allDone) return;
    if (this.inputManager.justDown(1, 'LIGHT')) this.completeHint('LIGHT');
    if (this.inputManager.justDown(1, 'HEAVY')) this.completeHint('HEAVY');
  }

  private tickBackupMeters(): void {
    tickBackupMeter(this.p1Backup);
    if (this.p2Backup) tickBackupMeter(this.p2Backup);

    if (isBackupReady(this.p1Backup) && this.inputManager.justDown(1, 'BACKUP')) {
      consumeBackupMeter(this.p1Backup);
      const cfg1 = getHeroSummonConfig(GameState.current.heroId);
      for (const spec of cfg1.allies) {
        this.summonAllies(spec.kind, spec.count, SUMMON_LIFESPAN_MS);
      }
      this.events.emit('backup-triggered', { player: 1 });
    }
    if (this.p2Backup && isBackupReady(this.p2Backup) && this.inputManager.justDown(2, 'BACKUP')) {
      consumeBackupMeter(this.p2Backup);
      const cfg2 = getHeroSummonConfig(GameState.current.p2HeroId);
      for (const spec of cfg2.allies) {
        this.summonAllies(spec.kind, spec.count, SUMMON_LIFESPAN_MS);
      }
      this.events.emit('backup-triggered', { player: 2 });
    }
  }

  // ── Summon allies ─────────────────────────────────────────────────────────

  /** Visual stats for each ally kind (placeholder rectangles until art lands). */
  private static readonly ALLY_STATS: Readonly<
    Record<string, { maxHp: number; speed: number; bodyW: number; bodyH: number; color: number }>
  > = {
    dog: { maxHp: 40, speed: 2.8, bodyW: 22, bodyH: 26, color: 0xc8a04a },
    crew: { maxHp: 80, speed: 1.8, bodyW: 26, bodyH: 44, color: 0x4a6a9a },
    rifleman: { maxHp: 60, speed: 1.6, bodyW: 26, bodyH: 44, color: 0x3a6a3a },
    fighter: { maxHp: 70, speed: 2.0, bodyW: 26, bodyH: 44, color: 0x9a4a4a },
    operative: { maxHp: 100, speed: 2.0, bodyW: 26, bodyH: 44, color: 0x2a3a4a },
  };

  /** Spawn `count` allies of `kind` near the player; they auto-despawn after `lifespanMs`. */
  private summonAllies(kind: AllyKind, count: number, lifespanMs: number): void {
    const stats = StageScene.ALLY_STATS[kind] ?? StageScene.ALLY_STATS['crew'];
    const recs = addAllies(this.summonState, kind, count, lifespanMs);
    const { height } = this.scale;
    const spawnY = height * 0.6;
    const originX = this.player?.x ?? this.levelWidth / 2;

    recs.forEach((rec, i) => {
      const offsetX = (i - Math.floor(count / 2)) * 60 + 80;
      const spawnX = Phaser.Math.Clamp(originX + offsetX, 60, this.levelWidth - 60);
      const ally = new Ally(this, spawnX, spawnY, {
        maxHp: stats.maxHp,
        lightPoiseThreshold: 3,
        color: stats.color,
        speed: stats.speed,
        bodyW: stats.bodyW,
        bodyH: stats.bodyH,
        // ally_<kind> static sprite if registered → Entity bodyImage + procedural
        // locomotion; falls back to the colour-rect when unregistered.
        assetId: `ally_${kind}`,
      });
      ally.setWorldWidth(this.levelWidth);
      ally.setCombatSystem(this.combat);
      const nearestIdx = findNearestTargetIndex(ally.x, ally.y, this.enemies);
      if (nearestIdx >= 0) ally.setTarget(this.enemies[nearestIdx]);
      this.combat.register(ally);
      this.depthSort.register(ally);
      this.allyEntities.set(rec.id, ally);
    });
  }

  /**
   * Update each ally's target to the nearest live enemy.
   * Called every frame so allies re-focus after an enemy dies.
   */
  private tickAllyTargets(): void {
    if (this.allyEntities.size === 0 || this.enemies.length === 0) return;
    for (const ally of this.allyEntities.values()) {
      const idx = findNearestTargetIndex(ally.x, ally.y, this.enemies);
      if (idx >= 0) ally.setTarget(this.enemies[idx]);
    }
  }

  /**
   * Cull KO'd allies and advance lifespan timers.
   * Expired allies fade out (poof) then destroy.
   */
  private tickAllyLifespans(delta: number): void {
    // Cull KO'd allies first (killed by enemies before lifespan ends).
    for (const [id, ally] of this.allyEntities) {
      if (ally.isKO) {
        this.combat.unregister(ally);
        this.depthSort.unregister(ally);
        ally.destroy();
        this.allyEntities.delete(id);
        removeAlly(this.summonState, id);
      }
    }

    // Advance lifespans and fade-despawn expired allies.
    const expired = tickAllies(this.summonState, delta);
    for (const id of expired) {
      const ally = this.allyEntities.get(id);
      if (!ally) continue;
      this.combat.unregister(ally);
      this.depthSort.unregister(ally);
      this.allyEntities.delete(id);
      // Fade poof — guard destroy in case scene shuts down mid-tween.
      this.tweens.add({
        targets: ally,
        alpha: 0,
        duration: 300,
        ease: 'Quad.easeIn',
        onComplete: () => {
          if (ally.scene) ally.destroy();
        },
      });
    }
  }

  private completeHint(id: HintId): void {
    if (!this.tutorialHints?.complete(id)) return;
    const t = this.hintTexts.get(id);
    if (!t) return;
    this.tweens.add({
      targets: t,
      alpha: 0,
      duration: 600,
      ease: 'Quad.easeIn',
      onComplete: () => t.destroy(),
    });
  }

  // ── Co-op helpers ────────────────────────────────────────────────────────

  private spawnPlayer2(heroId: string, x: number, y: number): void {
    const hero2 = getHero(heroId) ?? HEROES[1];
    this.hero2Color = hero2.color;
    this.hero2Name = hero2.name;
    this.player2 = new Player(this, x, y, hero2, this.inputManager, 2);
    this.player2.setWorldWidth(this.levelWidth);
    this.player2.attachCombat(this.combat);
    this.player2.attachProjectileManager(this.projectiles);
    this.combat.register(this.player2);
    this.depthSort.register(this.player2);
    if (!this.tagTracker) {
      this.tagTracker = new TagComboTracker();
    }
    this.p2Backup = createBackupMeter();
    this.dropInPrompt?.destroy();
    this.dropInPrompt = undefined;
    this.dropInPromptAdded = false;
  }

  /**
   * Mid-stage P2 drop-in: if co-op mode is active, p2HeroId is set, and P2
   * hasn't spawned yet, show a prompt and listen for their button press.
   */
  private checkDropIn(): void {
    const run = GameState.current;
    if (!run.coopMode || !run.p2HeroId || this.player2) return;

    if (!this.dropInPromptAdded) {
      this.dropInPromptAdded = true;
      const { width, height } = this.scale;
      this.dropInPrompt = this.add
        .text(width / 2, height - 60, 'P2: Press [K] / [Arrow] to join!', {
          fontFamily: '"Courier New", monospace',
          fontSize: '16px',
          color: '#00d4ff',
          stroke: '#000000',
          strokeThickness: 2,
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(600);
    }

    const p2Joined =
      this.inputManager.justDown(2, 'LIGHT') ||
      this.inputManager.justDown(2, 'JUMP') ||
      this.inputManager.justDown(2, 'HEAVY');

    if (p2Joined) {
      const spawnX = Math.min((this.player?.x ?? 400) + 80, this.levelWidth - 60);
      const { height } = this.scale;
      this.spawnPlayer2(run.p2HeroId, spawnX, height * 0.6);
    }
  }

  private trackTagCombo(attackerId?: number, targetId?: number): void {
    if (!this.tagTracker || attackerId === undefined || targetId === undefined) return;
    const run = GameState.current;

    let heroId: string | null = null;
    if (this.player && attackerId === this.player.combatId) {
      heroId = run.heroId;
    } else if (this.player2 && attackerId === this.player2.combatId) {
      heroId = run.p2HeroId;
    }
    if (!heroId) return;

    const finisher = this.tagTracker.recordHit(attackerId, heroId, targetId, this.frame);
    if (finisher) this.showTagFinisher(finisher);
  }

  private showTagFinisher(finisher: TagFinisher): void {
    if (!this.tagFinisherText) return;
    this.tagFinisherText.setText(finisher.name).setAlpha(1);
    this.tweens.killTweensOf(this.tagFinisherText);
    this.tweens.add({
      targets: this.tagFinisherText,
      alpha: 0,
      delay: 1400,
      duration: 350,
      ease: 'Quad.easeIn',
    });
    // Brief cinematic zoom.
    const cam = this.cameras.main;
    cam.zoomTo(1.12, 70, 'Linear', true, (_cam, progress) => {
      if (progress >= 1) cam.zoomTo(1, 140, 'Linear', true);
    });
    cam.flash(60, 255, 215, 0, true);
  }

  private updateCameraAnchor(): void {
    if (!this.player) return;
    const x = this.player2 ? (this.player.x + this.player2.x) / 2 : this.player.x;
    this.cameraAnchor.setPosition(x, this.player.y);
  }

  // ── HUD ─────────────────────────────────────────────────────────────────

  /** Create a visual world pickup for a weapon at (wx, wy). */
  private spawnWorldWeapon(wx: number, wy: number, def: WeaponDef): void {
    // weapon_<id> sprite if registered (getTexture null-safe → colour-rect fallback).
    // Sprite is authored at 26:10 so setDisplaySize(26,10) scales uniformly.
    const texKey = spriteAtlas.getTexture(`weapon_${def.id}`);
    const visual = texKey
      ? this.add.image(0, 0, texKey).setDisplaySize(26, 10)
      : this.add.rectangle(0, 0, 26, 10, def.color).setStrokeStyle(1, 0xffffff);
    const label = this.add
      .text(0, -14, def.label, { fontSize: '10px', color: '#ffffff', align: 'center' })
      .setOrigin(0.5);
    const container = this.add.container(wx, wy, [visual, label]).setDepth(1);
    this.worldWeapons.push({ def, x: wx, y: wy, container });
  }

  /**
   * Find the nearest world weapon within PICKUP_RANGE of px.
   * Returns an object with the def and a `remove()` that destroys the pickup.
   */
  private findNearestWorldWeapon(px: number): { def: WeaponDef; remove: () => void } | null {
    let best: (typeof this.worldWeapons)[0] | null = null;
    let bestDist = Infinity;
    for (const w of this.worldWeapons) {
      const dist = Math.abs(px - w.x);
      if (dist <= PICKUP_RANGE && dist < bestDist) {
        bestDist = dist;
        best = w;
      }
    }
    if (!best) return null;
    const found = best;
    return {
      def: found.def,
      remove: () => {
        found.container.destroy();
        this.worldWeapons = this.worldWeapons.filter((w) => w !== found);
      },
    };
  }

  /** Compose and commit the current HUD snapshot to the Phaser registry. */
  private writeHudRegistry(): void {
    if (!this.player) return;
    const run = GameState.current;
    const extraLives = GameState.consumeExtraLives();
    if (extraLives > 0) {
      this.sound.play(SFX_1UP_KEY, { volume: 0.9 });
    }
    const payload: HudPayload = {
      p1: {
        hpFrac: this.player.health.fraction,
        greenHpFrac: this.player.greenHpAmount / this.player.health.max,
        stars: this.player.starCount,
        score: run.score,
        lives: run.lives,
        heroColor: this.heroColor,
        heroName: this.heroName,
        backupFrac: backupFraction(this.p1Backup),
      },
      p2: this.player2
        ? {
            hpFrac: this.player2.health.fraction,
            greenHpFrac: this.player2.greenHpAmount / this.player2.health.max,
            stars: this.player2.starCount,
            score: run.score,
            lives: run.lives,
            heroColor: this.hero2Color,
            heroName: this.hero2Name,
            backupFrac: this.p2Backup ? backupFraction(this.p2Backup) : 0,
          }
        : null,
      stageName:
        run.gameMode === 'boss-rush'
          ? 'BOSS RUSH'
          : run.gameMode === 'survival'
            ? 'SURVIVAL'
            : this.stageName,
      wave:
        run.gameMode === 'boss-rush'
          ? run.bossRushIndex + 1
          : run.gameMode === 'survival'
            ? run.survivalWave
            : this.waveManager
              ? this.waveManager.currentIndex() + 1
              : 1,
      bossHpFrac: this.boss && !this.boss.isKO ? this.boss.hpFraction : 0,
      eliteHpFrac:
        this.activeElite && !this.activeElite.enemy.isKO
          ? this.activeElite.enemy.health.fraction
          : 0,
      eliteName: this.activeElite && !this.activeElite.enemy.isKO ? this.activeElite.name : '',
      extraLifeFlash: extraLives > 0,
      scoreMultiplier: run.gameMode === 'score-attack' ? scoreAttackStageMultiplier(run.stage) : 1,
    };
    this.registry.set(HUD_REGISTRY_KEY, payload);
  }

  // ── Wave system ──────────────────────────────────────────────────────────

  /** Spawn all enemies for the current WaveManager wave, spread across the level. */
  private spawnCurrentWave(): void {
    if (!this.waveManager || this.waveManager.allWavesCleared()) return;
    const { height } = this.scale;
    const wave = this.waveManager.currentWave();
    const mods = this.diffMods;
    let slotIndex = 0;

    const scaledGroups = wave.enemies.map((g) => ({
      id: g.id,
      count: Math.max(1, Math.round(g.count * mods.enemyCountMult)),
    }));
    const totalCount = scaledGroups.reduce((s, g) => s + g.count, 0);
    const spacing = Math.max(160, (this.levelWidth * 0.55) / Math.max(1, totalCount));

    scaledGroups.forEach((group) => {
      const def = getEnemyDef(group.id);
      for (let i = 0; i < group.count; i += 1) {
        const ex = this.levelWidth * 0.45 + slotIndex * spacing;
        const ey = height * (0.5 + (slotIndex % 2) * 0.12);
        const e = new Enemy(this, ex, ey, {
          maxHp: Math.round(def.maxHp * mods.enemyHpMult),
          lightPoiseThreshold: def.lightPoiseThreshold ?? getDefaultPoiseThreshold(def.tier),
          color: def.color,
          speed: def.speed * mods.enemySpeedMult,
          bodyW: def.bodyW,
          bodyH: def.bodyH,
          assetId: def.assetId,
          poseAssetIds: def.poseAssetIds,
        });
        e.setWorldWidth(this.levelWidth);
        e.setTarget(this.player!);
        this.combat.register(e);
        this.depthSort.register(e);
        this.enemies.push(e);
        if (def.tier === 'elite') {
          this.activeElite = { enemy: e, name: def.name };
        }
        slotIndex += 1;
      }
    });
  }

  /** Remove KO'd enemies each frame; notify WaveManager of each defeat. */
  private cullKOEnemies(): void {
    let defeated = 0;
    let finaleHallowayDefeated = false;
    this.enemies = this.enemies.filter((e) => {
      if (e.isKO) {
        this.combat.unregister(e);
        this.depthSort.unregister(e);
        if (e === this.finaleHalloway) {
          finaleHallowayDefeated = true;
        } else {
          defeated += 1;
        }
        return false;
      }
      return true;
    });
    const scoreDefeats = defeated + (finaleHallowayDefeated ? 1 : 0);
    if (scoreDefeats > 0) GameState.addScore(scoreDefeats * ENEMY_KO_SCORE);
    if (this.waveManager) {
      for (let i = 0; i < defeated; i += 1) this.waveManager.enemyDefeated();
    }
    if (finaleHallowayDefeated) {
      this.finaleHalloway = undefined;
      this.finaleHallowayCleared();
    }
  }

  /**
   * Called each frame after cullKOEnemies.
   * When remainingInWave reaches 0, advance to the next wave or clear the stage.
   */
  private tickWaveAdvance(): void {
    if (!this.waveManager) return;
    // Don't credit a wave-clear on a frame where the player is dying/transitioning
    // (same-frame KO + last-enemy-down would otherwise grant a phantom wave + bonus).
    if (this.transitioning || this.playerDeathHandled) return;
    if (this.waveManager.remainingInWave() > 0) return;
    if (this.waveManager.allWavesCleared()) return;

    // Section mini-boss: when a non-terminal section's last wave clears, spawn the
    // section elite and HOLD the advance until it's KO'd. The elite spawns after
    // `remaining` hit 0, so culling it floors at 0 and can't corrupt the count.
    if (this.awaitingSectionElite) {
      if (this.activeElite && !this.activeElite.enemy.isKO) return; // still fighting it
      this.awaitingSectionElite = false; // elite down → fall through to advance + checkpoint
    } else {
      const sectionEliteId = this.waveManager.eliteIdForSectionAboutToClear();
      if (sectionEliteId) {
        this.spawnSectionElite(sectionEliteId);
        this.awaitingSectionElite = true;
        return;
      }
    }

    const result = this.waveManager.advance();
    if (result === 'stage-complete') {
      if (this.isFinaleGauntletActive()) {
        this.finaleCordonCleared();
      } else if (this.stageCfgBossId) {
        this.spawnBoss(this.stageCfgBossId);
      } else {
        this.clearStageTransition();
      }
    } else {
      if (result === 'section-cleared') {
        this.showCheckpointBanner();
        GameState.setCheckpoint(this.waveManager.currentSectionIndex());
      }
      this.spawnCurrentWave();
    }
  }

  private isFinaleGauntletActive(): boolean {
    return this.finaleGauntletConfig !== undefined && this.finaleGauntletState !== undefined;
  }

  private finaleCordonCleared(): void {
    if (!this.finaleGauntletConfig || !this.finaleGauntletState) return;
    this.finaleGauntletState = saveGauntletCheckpoint(this.finaleGauntletState);
    this.finaleGauntletState = advanceGauntletPhase(this.finaleGauntletState);
    this.finaleGauntletState = saveGauntletCheckpoint(this.finaleGauntletState);
    this.showCheckpointBanner();
    this.enterFinaleGauntletPhase();
  }

  private enterFinaleGauntletPhase(): void {
    if (!this.finaleGauntletConfig || !this.finaleGauntletState) return;
    if (isGauntletComplete(this.finaleGauntletState, this.finaleGauntletConfig)) {
      this.clearStageTransition();
      return;
    }

    const phase = currentGauntletPhase(this.finaleGauntletState, this.finaleGauntletConfig);
    if (!phase) {
      this.clearStageTransition();
      return;
    }

    if (phase.kind === 'boss-rush') {
      const bossId = currentBossRushBossId(this.finaleGauntletState, this.finaleGauntletConfig);
      if (bossId) this.spawnBoss(bossId);
    } else if (phase.kind === 'praetor-handoff') {
      this.finaleGauntletState = saveGauntletCheckpoint(this.finaleGauntletState);
      this.showCheckpointBanner();
      // TODO(MY-1977): replace the generic fallback with Praetor's bespoke Phaser brain if needed.
      this.spawnBoss(phase.nextBossId, { allowGenericFallback: true });
    } else if (phase.kind === 'pm-halloway') {
      this.spawnFinaleHalloway(phase.nextBossId);
    }
  }

  private finaleBossCleared(): void {
    if (!this.finaleGauntletConfig || !this.finaleGauntletState) return;
    const phase = currentGauntletPhase(this.finaleGauntletState, this.finaleGauntletConfig);
    if (!phase) {
      this.clearStageTransition();
      return;
    }

    if (phase.kind === 'boss-rush') {
      this.finaleGauntletState = advanceFinaleBossRush(
        this.finaleGauntletState,
        this.finaleGauntletConfig,
      );
      this.time.delayedCall(700, () => this.enterFinaleGauntletPhase());
    } else if (phase.kind === 'praetor-handoff') {
      this.finaleGauntletState = advanceGauntletPhase(this.finaleGauntletState);
      this.time.delayedCall(700, () => this.enterFinaleGauntletPhase());
    }
  }

  private finaleHallowayCleared(): void {
    if (!this.finaleGauntletConfig || !this.finaleGauntletState) return;
    const phase = currentGauntletPhase(this.finaleGauntletState, this.finaleGauntletConfig);
    if (phase?.kind !== 'pm-halloway') return;
    this.finaleGauntletState = advanceGauntletPhase(this.finaleGauntletState);
    this.time.delayedCall(700, () => this.enterFinaleGauntletPhase());
  }

  private respawnFinaleGauntletAtCheckpoint(): GauntletPhase | null {
    if (!this.finaleGauntletConfig || !this.finaleGauntletState) return null;
    this.finaleGauntletState = respawnAtGauntletCheckpoint(this.finaleGauntletState);
    return currentGauntletPhase(this.finaleGauntletState, this.finaleGauntletConfig);
  }

  private showCheckpointBanner(): void {
    this.musicDir.onCheckpoint();
    if (!this.checkpointBanner) return;
    this.checkpointBanner.setAlpha(1);
    this.tweens.killTweensOf(this.checkpointBanner);
    this.tweens.add({
      targets: this.checkpointBanner,
      alpha: 0,
      delay: 1800,
      duration: 400,
      ease: 'Quad.easeIn',
    });
  }

  // ── Player death / checkpoint respawn ────────────────────────────────────

  /**
   * Detect player KO each frame. On life lost: respawn at checkpoint section.
   * On game-over: route to Results. Gated by playerDeathHandled to run once.
   */
  private tickPlayerDeath(): void {
    if (this.transitioning || this.playerDeathHandled) return;
    if (!this.player?.isKO) return;
    this.playerDeathHandled = true;

    GameState.loseLife();
    if (GameState.isGameOver) {
      this.transitioning = true;
      this.time.delayedCall(600, () => {
        this.scene.stop('HudScene');
        this.scene.start('ResultsScene');
      });
    } else {
      this.time.delayedCall(600, () => {
        this.respawnAtCheckpoint();
      });
    }
  }

  /**
   * Respawn the player at the start of the current checkpoint section.
   * Earlier sections remain cleared — only the in-progress section restarts.
   */
  private respawnAtCheckpoint(): void {
    // Remove all live enemies.
    this.enemies.forEach((e) => {
      this.combat.unregister(e);
      this.depthSort.unregister(e);
      e.destroy();
    });
    this.enemies = [];
    this.finaleHalloway = undefined;

    // Remove all active allies.
    for (const ally of this.allyEntities.values()) {
      this.combat.unregister(ally);
      this.depthSort.unregister(ally);
      ally.destroy();
    }
    this.allyEntities.clear();
    this.summonState = createSummonState();

    // Remove boss if active (player died during boss fight).
    if (this.boss) {
      this.combat.unregister(this.boss);
      this.depthSort.unregister(this.boss);
      this.boss.destroy();
      this.boss = undefined;
      this.bossKOHandled = false;
    }

    const finaleRespawnPhase = this.respawnFinaleGauntletAtCheckpoint();

    // Reset wave manager to the checkpoint section while the cordon is active.
    // Boss-rush/Praetor checkpoints resume directly into their boss phase.
    if (!finaleRespawnPhase || finaleRespawnPhase.kind === 'cordon') {
      this.waveManager?.resetToSection(GameState.current.checkpointSection);
    }
    // Re-arm the section-elite gate: the restarted section re-clears its waves
    // and re-spawns its elite. Clear the stale activeElite ref (its enemy was just
    // destroyed in the enemy cull above) so the HUD elite bar doesn't linger.
    this.awaitingSectionElite = false;
    this.activeElite = undefined;

    // Revive player at their original spawn position.
    const { width, height } = this.scale;
    this.player!.revive();
    this.player!.x = width * 0.3;
    this.player!.y = height * 0.6;

    // Co-op: a KO'd P2 must be revived on the shared-life checkpoint respawn too —
    // tickPlayerDeath only watches P1, so a P2 that went down stays KO'd (frozen)
    // for the rest of the life otherwise. Reposition next to P1's spawn.
    if (this.player2?.isKO) {
      this.player2.revive();
      this.player2.x = width * 0.3 + 70;
      this.player2.y = height * 0.6;
    }

    // Reset floating bars — enemy set has changed.
    this.enemyHealthBarPool?.reset();

    // Allow death detection again and spawn the section's first wave.
    this.playerDeathHandled = false;
    if (finaleRespawnPhase && finaleRespawnPhase.kind !== 'cordon') {
      this.enterFinaleGauntletPhase();
    } else {
      this.spawnCurrentWave();
    }
  }

  // ── Boss ─────────────────────────────────────────────────────────────────

  /** Spawn the stage boss; register with combat + depth sort; wire summon event. */
  private spawnBoss(id: string, opts: { allowGenericFallback?: boolean } = {}): void {
    const brain =
      createBossBrain(id) ?? (opts.allowGenericFallback ? new GenericBossBrain() : null);
    if (!brain) {
      // No brain registered for this id — skip boss and clear the stage.
      this.clearStageTransition();
      return;
    }
    this.bossKOHandled = false;
    this.musicDir.onBossTriggered(id);
    const def = getEnemyDef(id);
    this.bossLabel = def.name;
    const { height } = this.scale;
    this.boss = new BossEnemy(this, this.levelWidth * 0.7, height * 0.6, brain, {
      maxHp: Math.round(def.maxHp * this.diffMods.enemyHpMult),
      lightPoiseThreshold: def.lightPoiseThreshold ?? getDefaultPoiseThreshold(def.tier),
      color: def.color,
      speed: def.speed * this.diffMods.enemySpeedMult,
      bodyW: def.bodyW,
      bodyH: def.bodyH,
      label: def.name,
      assetId: def.assetId,
      poseAssetIds: def.poseAssetIds,
    });
    this.boss.setWorldWidth(this.levelWidth);
    this.boss.setTarget(this.player!);
    this.boss.setCombatSystem(this.combat);
    this.boss.setAggression(0.7);
    this.combat.register(this.boss);
    this.depthSort.register(this.boss);

    // off-before-on: a boss can be re-spawned within one scene instance
    // (death + checkpoint respawn), which would otherwise stack duplicate handlers.
    this.events.off('boss-wants-summon');
    this.events.on('boss-wants-summon', ({ bossX, bossY }: { bossX: number; bossY: number }) => {
      this.spawnBossAdds(bossX, bossY);
    });
  }

  /** Spawn the weak, non-boss PM Halloway coda entity after The Praetor falls. */
  private spawnFinaleHalloway(id: string): void {
    const def = getEnemyDef(id);
    const { height } = this.scale;
    const ex = Phaser.Math.Clamp(this.player!.x + 260, 80, this.levelWidth - 80);
    const e = new Enemy(this, ex, height * 0.6, {
      maxHp: def.maxHp,
      lightPoiseThreshold: 1,
      color: def.color,
      speed: def.speed,
      bodyW: def.bodyW,
      bodyH: def.bodyH,
      assetId: def.assetId,
      poseAssetIds: def.poseAssetIds,
      pmHalloway: def.pmHalloway,
    });
    e.setWorldWidth(this.levelWidth);
    e.setTarget(this.player!);
    e.setCombatSystem(this.combat);
    e.setAggression(0);
    this.combat.register(e);
    this.depthSort.register(e);
    this.enemies.push(e);
    this.finaleHalloway = e;
  }

  /** Halloway's joke super: a Praetorian appears, refuses the job, and walks off. */
  private spawnCallSecurityGag(): void {
    if (!this.player) return;
    const key = spriteAtlas.getTexture('manor-praetorian');
    if (!key) return;
    const cam = this.cameras.main;
    const startX = Phaser.Math.Clamp(this.player.x + 150, 80, this.levelWidth - 80);
    const startY = this.player.y + 4;
    const guard = this.add.image(startX, startY, key).setOrigin(0.5, 1).setDepth(410);
    guard.setDisplaySize(94, 126);
    guard.setFlipX(true);
    guard.setAlpha(0);
    cam.flash(90, 120, 180, 255, true);
    this.tweens.add({
      targets: guard,
      alpha: 1,
      duration: 80,
      yoyo: true,
      hold: 360,
      onComplete: () => {
        this.tweens.add({
          targets: guard,
          x: startX + 260,
          y: startY + 8,
          alpha: 0,
          duration: 700,
          ease: 'Sine.easeIn',
          onComplete: () => guard.destroy(),
        });
      },
    });
  }

  /** Spawn 2 add enemies near the boss position when the boss requests reinforcements. */
  private spawnBossAdds(nearX: number, nearY: number): void {
    const def = getEnemyDef('camden-trader');
    const mods = this.diffMods;
    for (let i = 0; i < 2; i += 1) {
      const offsetX = (i === 0 ? -1 : 1) * 120;
      const ex = Phaser.Math.Clamp(nearX + offsetX, 60, this.levelWidth - 60);
      const e = new Enemy(this, ex, nearY, {
        maxHp: Math.round(def.maxHp * mods.enemyHpMult),
        lightPoiseThreshold: def.lightPoiseThreshold ?? getDefaultPoiseThreshold(def.tier),
        color: def.color,
        speed: def.speed * mods.enemySpeedMult,
        bodyW: def.bodyW,
        bodyH: def.bodyH,
        assetId: def.assetId,
        poseAssetIds: def.poseAssetIds,
      });
      e.setWorldWidth(this.levelWidth);
      e.setTarget(this.player!);
      this.combat.register(e);
      this.depthSort.register(e);
      this.enemies.push(e);
    }
  }

  /**
   * Spawn a section mini-boss elite (high HP, ignores hitstun) at the end of a
   * section, before its checkpoint. Its sprite sheet is already preloaded via
   * stageAnimCharIds(section.eliteId). Tracked as activeElite so the HUD elite
   * bar shows; tickWaveAdvance holds the section advance until it's KO'd.
   */
  private spawnSectionElite(eliteId: string): void {
    const def = getEnemyDef(eliteId);
    if (!def) return;
    const mods = this.diffMods;
    const { height } = this.scale;
    const ex = Phaser.Math.Clamp(this.player!.x + 320, 80, this.levelWidth - 80);
    const e = new Enemy(this, ex, height * 0.58, {
      maxHp: Math.round(def.maxHp * mods.enemyHpMult),
      lightPoiseThreshold: def.lightPoiseThreshold ?? getDefaultPoiseThreshold(def.tier),
      color: def.color,
      speed: def.speed * mods.enemySpeedMult,
      bodyW: def.bodyW,
      bodyH: def.bodyH,
      assetId: def.assetId,
      poseAssetIds: def.poseAssetIds,
    });
    e.setWorldWidth(this.levelWidth);
    e.setTarget(this.player!);
    this.combat.register(e);
    this.depthSort.register(e);
    this.enemies.push(e);
    this.activeElite = { enemy: e, name: def.name };
  }

  /**
   * Cosmetic-only: animate a thrown weapon flying from the hand to its landing
   * point. The damage is the instant hitbox spawned in Player.throwWeapon; this
   * just makes the throw legible. Tween + container are scene-owned, so Phaser's
   * shutdown clears them on a mode restart (no manual cleanup needed).
   */
  private spawnThrownWeaponVisual(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    def: WeaponDef,
  ): void {
    const v = thrownWeaponVisual(def);
    if (!v) return;
    const dir = toX >= fromX ? 1 : -1;
    const body = this.add.container(fromX, fromY).setDepth(900);
    if (v.kind === 'molotov') {
      body.add(this.add.rectangle(0, 0, 9, 14, v.color).setStrokeStyle(1, 0x2a1500));
      body.add(this.add.ellipse(0, -10, 7, 11, 0xffb020).setName('flame'));
    } else if (v.kind === 'brick') {
      body.add(this.add.rectangle(0, 0, 16, 10, v.color).setStrokeStyle(1, 0x000000, 0.4));
    } else {
      body.add(this.add.rectangle(0, 0, 22, 4, v.color)); // blade
      body.add(this.add.rectangle(-9, 0, 5, 6, 0x3a2a18)); // hilt
    }
    const apex = v.arc ? 60 : 0;
    this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: v.durationMs,
      ease: 'Linear',
      onUpdate: (tw) => {
        const t = tw.getValue() ?? 0;
        body.x = fromX + (toX - fromX) * t;
        body.y = fromY + (toY - fromY) * t - Math.sin(t * Math.PI) * apex;
        if (v.spin) body.angle = dir * t * 720;
        if (v.flame) {
          const f = body.getByName('flame') as Phaser.GameObjects.Ellipse | null;
          if (f) f.setScale(0.8 + 0.4 * Math.abs(Math.sin(t * 30)));
        }
      },
      onComplete: () => body.destroy(),
    });
  }

  /**
   * Check whether the active boss has been KO'd; if so, clear the stage.
   * Called once per frame after cullKOEnemies and tickWaveAdvance.
   */
  private tickBossCheck(): void {
    if (this.transitioning) return;
    if (!this.boss || this.bossKOHandled) return;
    if (!this.boss.isKO) return;
    this.bossKOHandled = true;
    GameState.addScore(BOSS_KO_SCORE);
    this.combat.unregister(this.boss);
    this.depthSort.unregister(this.boss);
    this.musicDir.onBossDefeated();
    if (this.isFinaleGauntletActive()) {
      this.boss.destroy();
      this.boss = undefined;
      this.bossLabel = '';
      this.finaleBossCleared();
      return;
    }
    this.clearStageTransition();
  }

  /**
   * Survival: a wave (or boss milestone) cleared — award the escalating bonus,
   * advance the wave counter, and loop StageScene into the next, harder wave.
   * The run only ends when the player is KO'd with no lives left (Results).
   */
  private survivalWaveCleared(): void {
    GameState.addScore(survivalWaveClearBonus(GameState.current.survivalWave));
    GameState.advanceSurvivalWave();
    this.showCheckpointBanner();
    this.time.delayedCall(900, () => {
      // HudScene is launched idempotently by key, so it persists across the restart.
      this.scene.start('StageScene', { gameMode: 'survival' });
    });
  }

  /**
   * Boss Rush: the sequence boss fell — advance the index and loop into the next
   * boss, or route to Results once all bosses are beaten.
   */
  private bossRushBossCleared(): void {
    GameState.advanceBossRush();
    const defeated = GameState.current.bossRushIndex;
    const route = bossRushNextRoute(defeated, BOSS_RUSH_SEQUENCE.length);
    this.showCheckpointBanner();
    this.time.delayedCall(900, () => {
      if (route.sceneKey === 'StageScene') {
        this.scene.start('StageScene', route.data);
      } else {
        this.scene.stop('HudScene');
        this.scene.start(route.sceneKey, route.data);
      }
    });
  }

  /**
   * Score Attack: a stage cleared — advance and route to the next stage (or to
   * Results after the final stage). Skips the campaign deal/cutscene flow; score
   * was already multiplied per-stage by GameState.addScore.
   */
  private scoreAttackStageCleared(): void {
    const cleared = GameState.current.stage;
    GameState.clearStage();
    const route = scoreAttackNextRoute(cleared, FINAL_STAGE);
    this.showCheckpointBanner();
    this.time.delayedCall(900, () => {
      if (route.sceneKey === 'StageScene') {
        this.scene.start('StageScene', route.data);
      } else {
        this.scene.stop('HudScene');
        this.scene.start(route.sceneKey, route.data);
      }
    });
  }

  /** Shared stage-clear teardown used by both wave-only and boss-kill paths. */
  private clearStageTransition(): void {
    if (this.transitioning) return;
    this.transitioning = true;
    if (GameState.current.gameMode === 'boss-rush') {
      this.bossRushBossCleared();
      return;
    }
    if (GameState.current.gameMode === 'score-attack') {
      this.scoreAttackStageCleared();
      return;
    }
    if (GameState.current.gameMode === 'survival') {
      this.survivalWaveCleared();
      return;
    }
    const stageJustCleared = GameState.current.stage;
    GameState.clearStage();
    getSaveManager().autoSave(
      ACTIVE_SLOT,
      stageJustCleared,
      GameState.current.heroId,
      GameState.current.tookDeal,
      Date.now(),
    );
    this.scene.stop('HudScene');
    const isFinal = GameState.isFinalStageCleared;
    const endCutsceneId = `stage${stageJustCleared}-end`;
    const route = nextRoute(stageJustCleared, hasCutscene(endCutsceneId), isFinal);
    this.scene.start(route.sceneKey, route.data);
  }

  // ── Hazards ──────────────────────────────────────────────────────────────

  /**
   * Moving-carriage hazard: periodic horizontal lurch that nudges all entities
   * left or right, simulating the carriage jolting on the tracks.
   * Called only when stageHazard === 'moving-carriage'; no-ops on other stages.
   * TODO(MY-1668): extend with animation/sound once art assets land.
   */
  private tickCarriageSway(): void {
    this.swayTimer += 1;
    if (this.swayTimer % CARRIAGE_SWAY_INTERVAL !== 0) return;
    const lurchCount = this.swayTimer / CARRIAGE_SWAY_INTERVAL;
    const nudge = lurchCount % 2 === 0 ? CARRIAGE_SWAY_AMOUNT : -CARRIAGE_SWAY_AMOUNT;
    const clamp = (x: number) => Phaser.Math.Clamp(x, 60, this.levelWidth - 60);
    if (this.player) this.player.x = clamp(this.player.x + nudge);
    if (this.player2) this.player2.x = clamp(this.player2.x + nudge);
    this.enemies.forEach((e) => {
      e.x = clamp(e.x + nudge);
    });
    if (this.boss && !this.boss.isKO) this.boss.x = clamp(this.boss.x + nudge);
    this.cameras.main.shake(180, 0.005);
  }

  // ── Debug ────────────────────────────────────────────────────────────────

  private drawDebug(): void {
    const g = this.debugGfx;
    if (!g) return;
    g.clear();
    const sx = this.cameras.main.scrollX;
    const sy = this.cameras.main.scrollY;

    const drawBox = (box: { x: number; y: number; w: number; h: number }) =>
      g.strokeRect(box.x - sx, box.y - sy, box.w, box.h);

    g.lineStyle(1, 0x06d6a0, 0.9);
    if (this.player) drawBox(this.player.worldHurtbox());
    if (this.player2) drawBox(this.player2.worldHurtbox());
    this.enemies.forEach((e) => drawBox(e.worldHurtbox()));
    if (this.boss && !this.boss.isKO) drawBox(this.boss.worldHurtbox());

    g.lineStyle(1, 0xef233c, 0.9);
    this.combat.activeHitboxes.forEach((hb) => drawBox(hb.box));
  }
}
